import React, { useState, useEffect, useMemo } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Tabs,
	Tab,
	Typography,
	LinearProgress,
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	Chip,
	CircularProgress,
} from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";

import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATEditButton from "../../components/Buttons/PATEditButton";
import PATPrintButton from "../../components/Buttons/PATPrintButton";
import { getFundingSourceLinkedRecords } from "./useFundingSourceData";

const TabPanel = ({ children, value, index }) =>
	value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;

const fmtC = (n) =>
	n != null && !isNaN(n) ? `$${Number(n).toLocaleString()}` : "—";

const fmtDate = (d) => (d ? moment(d).format("MM/DD/YYYY") : "—");

const StatusChip = ({ status }) => {
	const colorMap = {
		Active: "success",
		Inactive: "default",
		Pending: "warning",
		Expired: "error",
		Open: "info",
		Closed: "default",
		Complete: "success",
		Completed: "success",
	};
	return (
		<Chip
			label={status || "—"}
			size="small"
			color={colorMap[status] || "default"}
			variant="outlined"
		/>
	);
};

const SectionHead = ({ children, count, amt }) => (
	<Box
		sx={{
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			mt: 2,
			mb: 1,
			pb: 0.5,
			borderBottom: "1px solid",
			borderColor: "divider",
		}}
	>
		<Typography
			variant="caption"
			fontWeight={800}
			sx={{
				textTransform: "uppercase",
				letterSpacing: "0.07em",
			}}
			color="text.secondary"
		>
			{children}{" "}
			<Typography
				component="span"
				variant="caption"
				color="text.disabled"
			>
				({count})
			</Typography>
		</Typography>
		{amt != null && (
			<Typography
				variant="caption"
				fontFamily="monospace"
				color="text.secondary"
			>
				{fmtC(amt)}
			</Typography>
		)}
	</Box>
);

const thStyle = {
	textAlign: "left",
	py: 0.5,
	px: 1,
	fontSize: 10,
	fontWeight: 700,
	color: "text.secondary",
	textTransform: "uppercase",
	borderBottom: "1px solid",
	borderColor: "divider",
};

const tdStyle = {
	py: 0.75,
	px: 1,
	fontSize: 12,
	borderBottom: "1px solid",
	borderColor: "divider",
};

const FundingDetailDialog = ({ open, onClose, record, onEdit, perms = {} }) => {
	const userAgency = useSelector((state) => state.user.userAgency);
	const { userData } = useSelector((state) => state.user);
	const printedBy = userData?.user?.Username || "Unknown";

	const [tab, setTab] = useState(0);
	const [loading, setLoading] = useState(false);
	const [linkedData, setLinkedData] = useState({
		vehicles: [],
		equipment: [],
		workOrders: [],
		maintenance: [],
		dispensableItems: [],
		dispensableIssuances: [],
	});

	// Fetch linked records when dialog opens
	useEffect(() => {
		if (open && record?.id) {
			setLoading(true);
			getFundingSourceLinkedRecords(record.id, userAgency)
				.then((data) => {
					setLinkedData(data);
				})
				.catch((error) => {
					console.error("Error fetching linked records:", error);
				})
				.finally(() => {
					setLoading(false);
				});
		}
	}, [open, record?.id, userAgency]);

	// Reset tab when dialog closes
	useEffect(() => {
		if (!open) {
			setTab(0);
			setLinkedData({
				vehicles: [],
				equipment: [],
				workOrders: [],
				maintenance: [],
				dispensableItems: [],
				dispensableIssuances: [],
			});
		}
	}, [open]);

	// Create item lookup map
	const itemMap = useMemo(() => {
		const map = {};
		(linkedData.dispensableItems || []).forEach((item) => {
			map[item.id] = item;
		});
		return map;
	}, [linkedData.dispensableItems]);

	// Enrich issuances with item info using useMemo
	const dispensableRestocksWithItems = useMemo(() => {
		if (
			!linkedData.dispensableIssuances ||
			linkedData.dispensableIssuances.length === 0
		) {
			return [];
		}

		return linkedData.dispensableIssuances
			.filter((iss) => iss.isRestock && iss.purchaseCost != null)
			.map((iss) => ({
				...iss,
				item: itemMap[iss.itemId] || {
					trackingNumber: "—",
					description: "Unknown Item",
					vendor: iss.vendor || "—",
				},
			}))
			.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
	}, [linkedData.dispensableIssuances, itemMap]);

	// Calculate spending from issuances directly
	const spendDisp = useMemo(() => {
		return (linkedData.dispensableIssuances || [])
			.filter((iss) => iss.isRestock && iss.purchaseCost != null)
			.reduce((s, iss) => s + (iss.purchaseCost || 0), 0);
	}, [linkedData.dispensableIssuances]);

	// NOW you can return early - after all hooks
	if (!record) return null;

	const { vehicles, equipment, workOrders, maintenance } = linkedData;

	// ── Date / progress calculations ──
	const today = new Date();
	const start = record.startDate ? new Date(record.startDate) : null;
	const end = record.endDate ? new Date(record.endDate) : null;
	const totalDays =
		start && end ? Math.max(1, Math.ceil((end - start) / 86400000)) : null;
	const elapsed = start
		? Math.max(0, Math.ceil((today - start) / 86400000))
		: null;
	const progressPct =
		totalDays && elapsed != null
			? Math.min(100, Math.round((elapsed / totalDays) * 100))
			: null;
	const daysLeft = end ? Math.ceil((end - today) / 86400000) : null;
	const progressColor =
		progressPct >= 90
			? "error.main"
			: progressPct >= 60
				? "warning.main"
				: "success.main";

	// ── Calculate spending from linked records ──
	const spendVehicles = vehicles.reduce(
		(s, v) => s + (v.originalCost || v.cost || 0),
		0,
	);
	const spendEquipment = equipment.reduce((s, e) => s + (e.cost || 0), 0);
	const spendMaint = maintenance.reduce((s, m) => s + (m.cost || 0), 0);
	const spendWOs = workOrders.reduce((s, w) => s + (w.cost || 0), 0);

	const totalSpent =
		spendVehicles + spendEquipment + spendMaint + spendWOs + spendDisp;
	const remaining = (record.amount || 0) - totalSpent;
	const spentPct = record.amount
		? Math.min(100, Math.round((totalSpent / record.amount) * 100))
		: 0;
	const spentColor =
		spentPct >= 90
			? "error.main"
			: spentPct >= 70
				? "warning.main"
				: "success.main";

	// ── Print handler ──
	const handlePrint = () => {
		const d = (v) => (v == null || v === "" ? "—" : v);
		const m = (n) =>
			n != null && !isNaN(n)
				? "$" +
					Number(n).toLocaleString(undefined, {
						minimumFractionDigits: 2,
						maximumFractionDigits: 2,
					})
				: "—";
		const dt = (v) => (v ? new Date(v).toLocaleDateString() : "—");

		const css = `
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: Arial, Helvetica, sans-serif; font-size: 11px; color: #1a1a1a; background: #fff; padding: 24px; }
      h1 { font-size: 20px; font-weight: 800; letter-spacing: 0.01em; margin-bottom: 2px; }
      .subtitle { font-size: 13px; color: #555; margin-bottom: 4px; }
      .meta-row { font-size: 10px; color: #888; margin-bottom: 18px; }
      .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 700; border: 1px solid #ccc; background: #f4f4f4; }
      .section-head { background: #1a1a1a; color: #fff; padding: 5px 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; margin: 18px 0 8px; border-radius: 3px; display: flex; justify-content: space-between; align-items: center; }
      .section-head .mono { font-family: monospace; font-weight: 400; font-size: 11px; }
      .fields-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 6px 16px; margin-bottom: 8px; }
      .field-row { display: flex; flex-direction: column; }
      .field-label { font-size: 9px; font-weight: 700; text-transform: uppercase; color: #888; letter-spacing: 0.06em; margin-bottom: 1px; }
      .field-value { font-size: 11px; color: #1a1a1a; }
      .desc-block { padding: 6px 10px; background: #f8f8f8; border-left: 3px solid #ddd; font-size: 11px; margin-bottom: 12px; }
      .stat-row { display: flex; gap: 12px; margin-bottom: 12px; }
      .stat-box { flex: 1; padding: 8px 12px; border: 1px solid #ddd; border-radius: 4px; }
      .stat-num { font-size: 16px; font-weight: 800; font-family: monospace; }
      .stat-label { font-size: 9px; text-transform: uppercase; color: #888; letter-spacing: 0.05em; }
      .bar-wrap { height: 6px; background: #e5e7eb; border-radius: 3px; overflow: hidden; margin-top: 6px; }
      .bar-fill { height: 6px; background: #1a1a1a; border-radius: 3px; }
      table { width: 100%; border-collapse: collapse; font-size: 10px; margin-bottom: 4px; }
      thead tr { background: #f0f0f0; }
      th { text-align: left; padding: 4px 6px; font-weight: 700; font-size: 9px; text-transform: uppercase; letter-spacing: 0.05em; color: #555; border-bottom: 1px solid #ddd; }
      th.right, td.right { text-align: right; }
      td { padding: 4px 6px; border-bottom: 1px solid #eee; vertical-align: top; }
      td.mono { font-family: monospace; }
      tr:nth-child(even) td { background: #fafafa; }
      .empty-note { color: #aaa; font-style: italic; font-size: 10px; padding: 4px 0; }
      .print-footer { position: fixed; bottom: 0; left: 0; right: 0; border-top: 1px solid #ddd; padding: 4px 12px; font-size: 9px; color: #aaa; background: #fff; display: flex; justify-content: space-between; }
      @media print {
        body { padding: 12px; }
        .section-head { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        thead tr { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        tr:nth-child(even) td { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        .bar-fill { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        @page { margin: 14mm 12mm 20mm; }
      }
    `;

		const sh = (title, amt) =>
			`<div class="section-head"><span>${title}</span>${amt != null ? `<span class="mono">${m(amt)}</span>` : ""}</div>`;

		const fieldRow = (label, value) =>
			`<div class="field-row"><span class="field-label">${label}</span><span class="field-value">${value}</span></div>`;

		const tbl = (headers, rows) => {
			if (!rows.length) return `<div class="empty-note">No records</div>`;
			return `<table><thead><tr>${headers.map((h) => `<th${h.right ? ' class="right"' : ""}>${h.label}</th>`).join("")}</tr></thead><tbody>${rows.map((r) => `<tr>${r.map((c, ci) => `<td${headers[ci]?.right ? ' class="right mono"' : headers[ci]?.mono ? ' class="mono"' : ""}>${c ?? "—"}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
		};

		const spentBar = record.amount
			? Math.min(100, Math.round((totalSpent / record.amount) * 100))
			: 0;

		const detailsHtml = `
      ${sh("Funding Source Details")}
      <div class="fields-grid">
        ${fieldRow("Source", d(record.source))}
        ${fieldRow("Administrator", d(record.administrator))}
        ${fieldRow("Start Date", dt(record.startDate))}
        ${fieldRow("End Date", dt(record.endDate))}
        ${fieldRow("Status", d(record.status))}
        ${fieldRow("Budget", m(record.amount))}
        ${fieldRow("Total Spent", m(totalSpent))}
        ${fieldRow("Remaining", m(remaining))}
      </div>
      ${record.description ? `<div class="desc-block">${record.description}</div>` : ""}
    `;

		const budgetHtml = `
      ${sh("Budget Utilization")}
      <div class="stat-row">
        <div class="stat-box"><div class="stat-num">${m(record.amount)}</div><div class="stat-label">Total Budget</div></div>
        <div class="stat-box"><div class="stat-num">${m(totalSpent)}</div><div class="stat-label">Total Spent</div></div>
        <div class="stat-box"><div class="stat-num">${m(remaining)}</div><div class="stat-label">Remaining</div></div>
        <div class="stat-box"><div class="stat-num">${spentBar}%</div><div class="stat-label">Utilized</div></div>
      </div>
      <div style="font-size:9px;color:#888;margin-bottom:4px">Breakdown: Vehicles ${m(spendVehicles)} · Equipment ${m(spendEquipment)} · Work Orders ${m(spendWOs)} · Maintenance ${m(spendMaint)} · Dispensable ${m(spendDisp)}</div>
      <div class="bar-wrap"><div class="bar-fill" style="width:${spentBar}%"></div></div>
    `;

		const vehiclesHtml =
			vehicles.length > 0
				? `
      ${sh(`Vehicles (${vehicles.length})`, spendVehicles)}
      ${tbl(
			[
				{ label: "Unit #", mono: true },
				{ label: "Vehicle" },
				{ label: "Division" },
				{ label: "Status" },
				{ label: "Original Cost", right: true },
			],
			vehicles.map((v) => [
				v.unitNumber,
				`${v.year || ""} ${v.make || ""} ${v.model || ""}`.trim() ||
					"—",
				d(v.division),
				d(v.status),
				m(v.originalCost || v.cost),
			]),
		)}`
				: "";

		const equipHtml =
			equipment.length > 0
				? `
      ${sh(`Equipment (${equipment.length})`, spendEquipment)}
      ${tbl(
			[
				{ label: "Property #", mono: true },
				{ label: "Type" },
				{ label: "Brand / Model" },
				{ label: "Condition" },
				{ label: "Cost", right: true },
			],
			equipment.map((e) => [
				e.propertyNumber || "—",
				d(e.type),
				[e.brand, e.model].filter(Boolean).join(" ") || "—",
				d(e.condition || e.status),
				m(e.cost),
			]),
		)}`
				: "";

		const woHtml =
			workOrders.length > 0
				? `
      ${sh(`Work Orders (${workOrders.length})`, spendWOs)}
      ${tbl(
			[
				{ label: "WO #", mono: true },
				{ label: "Vehicle" },
				{ label: "Type" },
				{ label: "Vendor" },
				{ label: "Opened", mono: true },
				{ label: "Status" },
				{ label: "Cost", right: true },
			],
			workOrders.map((w) => {
				const veh = vehicles.find((v) => v.id === w.vehicleId);
				return [
					"WO-" + String(w.id).slice(-4).padStart(4, "0"),
					veh ? veh.unitNumber : "—",
					d(w.type),
					d(w.vendor),
					dt(w.openedDate),
					d(w.status),
					m(w.cost),
				];
			}),
		)}`
				: "";

		const maintHtml =
			maintenance.length > 0
				? `
      ${sh(`Maintenance (${maintenance.length})`, spendMaint)}
      ${tbl(
			[
				{ label: "Date", mono: true },
				{ label: "Description" },
				{ label: "Performed By" },
				{ label: "Status" },
				{ label: "Cost", right: true },
			],
			maintenance.map((r) => [
				dt(r.date),
				d(r.description?.slice(0, 60)),
				d(r.performedBy),
				d(r.status),
				m(r.cost),
			]),
		)}`
				: "";

		const dispHtml =
			dispensableRestocksWithItems.length > 0
				? `
      ${sh(`Dispensable Items — Restocks (${dispensableRestocksWithItems.length})`, spendDisp)}
      ${tbl(
			[
				{ label: "Date", mono: true },
				{ label: "Tracking #", mono: true },
				{ label: "Item" },
				{ label: "Vendor" },
				{ label: "Qty", right: true },
				{ label: "Purchase Cost", right: true },
			],
			dispensableRestocksWithItems.map((iss) => [
				dt(iss.date),
				iss.item.trackingNumber,
				d(iss.item.description),
				d(iss.item.vendor),
				Math.abs(iss.quantity),
				m(iss.purchaseCost),
			]),
		)}`
				: "";

		const noLinked =
			!vehiclesHtml && !equipHtml && !woHtml && !maintHtml && !dispHtml
				? `<div class="empty-note" style="padding:12px 0;text-align:center">No records linked to this funding source.</div>`
				: "";

		const html = `<!DOCTYPE html><html><head>
<meta charset="utf-8">
<title>Funding Source — ${d(record.title)}</title>
<style>${css}</style>
</head><body>
<h1>${d(record.title)}</h1>
<div class="subtitle">Funding Source Record <span class="badge">${d(record.status)}</span></div>
<div class="meta-row">Printed ${new Date().toLocaleString()} · Printed by ${printedBy || "Unknown"}</div>
${detailsHtml}${budgetHtml}${vehiclesHtml}${equipHtml}${woHtml}${maintHtml}${dispHtml}${noLinked}
<div class="print-footer">
  <span>Asset Management · Funding Sources · ${d(record.title)}</span>
  <span>Printed ${new Date().toLocaleString()} · ${printedBy || "Unknown"}</span>
</div>
<script>window.onload = function() { window.print(); };</script>
</body></html>`;

		const w = window.open("", "_blank", "width=1100,height=750");
		if (w) {
			w.document.write(html);
			w.document.close();
		}
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>
				{record.title || "Funding Source Details"}
			</DialogTitle>

			<Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
				<Tabs
					value={tab}
					onChange={(_, v) => setTab(v)}
					variant="scrollable"
					scrollButtons="auto"
				>
					<Tab label="Overview" />
					<Tab label="Budget" />
					<Tab label={`Vehicles (${vehicles.length})`} />
					<Tab label={`Equipment (${equipment.length})`} />
					<Tab label={`Work Orders (${workOrders.length})`} />
					<Tab label={`Maintenance (${maintenance.length})`} />
					<Tab
						label={`Dispensable (${dispensableRestocksWithItems.length})`}
					/>
				</Tabs>
			</Box>

			<DialogContent>
				{/* Loading indicator */}
				{loading && (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							py: 4,
						}}
					>
						<CircularProgress size={32} />
						<Typography variant="body2" sx={{ ml: 2 }}>
							Loading linked records...
						</Typography>
					</Box>
				)}

				{!loading && (
					<>
						{/* ── Overview Tab ── */}
						<TabPanel value={tab} index={0}>
							{/* Header */}
							<Box
								sx={{
									display: "flex",
									alignItems: "baseline",
									gap: 2,
									mb: 2,
								}}
							>
								<Typography
									variant="h4"
									fontWeight={800}
									fontFamily="monospace"
								>
									{fmtC(record.amount)}
								</Typography>
								<StatusChip status={record.status} />
							</Box>

							{record.description && (
								<Box
									sx={{
										p: 1.5,
										mb: 2,
										bgcolor: "grey.50",
										borderLeft: "3px solid",
										borderColor: "divider",
										borderRadius: 1,
									}}
								>
									<Typography variant="body2">
										{record.description}
									</Typography>
								</Box>
							)}

							{/* Detail fields */}
							<Box
								sx={{
									display: "grid",
									gridTemplateColumns: "repeat(2, 1fr)",
									gap: 2,
									mb: 2,
								}}
							>
								{[
									["Source", record.source || "—"],
									[
										"Administrator",
										record.administrator || "—",
									],
									["Start Date", fmtDate(record.startDate)],
									["End Date", fmtDate(record.endDate)],
								].map(([label, value]) => (
									<Box key={label}>
										<Typography
											variant="caption"
											fontWeight={700}
											color="text.secondary"
											sx={{
												textTransform: "uppercase",
												letterSpacing: "0.06em",
												display: "block",
												mb: 0.25,
											}}
										>
											{label}
										</Typography>
										<Typography
											variant="body2"
											fontFamily={
												label.includes("Date")
													? "monospace"
													: undefined
											}
										>
											{value}
										</Typography>
									</Box>
								))}
							</Box>

							{/* Period progress */}
							{progressPct != null && (
								<Box
									sx={{
										p: 2,
										border: "1px solid",
										borderColor: "divider",
										borderRadius: 1,
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											mb: 0.5,
										}}
									>
										<Typography
											variant="caption"
											fontWeight={700}
											color="text.secondary"
										>
											Period Progress
										</Typography>
										<Typography
											variant="caption"
											fontFamily="monospace"
										>
											{progressPct}%
											{daysLeft != null && daysLeft > 0
												? ` · ${daysLeft}d remaining`
												: daysLeft != null &&
													  daysLeft <= 0
													? " · Ended"
													: ""}
										</Typography>
									</Box>
									<LinearProgress
										variant="determinate"
										value={progressPct}
										sx={{
											height: 6,
											borderRadius: 3,
											bgcolor: "grey.200",
											"& .MuiLinearProgress-bar": {
												bgcolor: progressColor,
												borderRadius: 3,
											},
										}}
									/>
								</Box>
							)}
						</TabPanel>

						{/* ── Budget Tab ── */}
						<TabPanel value={tab} index={1}>
							<Box
								sx={{
									p: 2,
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1,
								}}
							>
								<Typography
									variant="caption"
									fontWeight={800}
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.07em",
										mb: 1.5,
										display: "block",
									}}
									color="text.secondary"
								>
									Budget Utilization
								</Typography>

								{/* Breakdown grid */}
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns: "1fr 1fr",
										gap: 1,
										mb: 2,
									}}
								>
									{[
										["Vehicles", spendVehicles],
										["Equipment", spendEquipment],
										["Work Orders", spendWOs],
										["Maintenance", spendMaint],
										["Dispensable", spendDisp],
									].map(([lbl, amt]) => (
										<Box key={lbl}>
											<Typography
												variant="body2"
												color="text.secondary"
												component="span"
											>
												{lbl}:{" "}
											</Typography>
											<Typography
												variant="body2"
												fontFamily="monospace"
												fontWeight={600}
												component="span"
											>
												{fmtC(amt)}
											</Typography>
										</Box>
									))}
								</Box>

								{/* Totals */}
								<Box
									sx={{
										display: "flex",
										justifyContent: "space-between",
										mb: 1,
									}}
								>
									<Typography
										variant="body2"
										fontWeight={700}
									>
										Total Spent:{" "}
										<Typography
											component="span"
											fontFamily="monospace"
											color={spentColor}
											fontWeight={700}
										>
											{fmtC(totalSpent)}
										</Typography>
									</Typography>
									<Typography
										variant="body2"
										fontWeight={700}
									>
										Remaining:{" "}
										<Typography
											component="span"
											fontFamily="monospace"
											color={
												remaining < 0
													? "error.main"
													: "success.main"
											}
											fontWeight={700}
										>
											{fmtC(remaining)}
										</Typography>
									</Typography>
								</Box>

								<LinearProgress
									variant="determinate"
									value={spentPct}
									sx={{
										height: 6,
										borderRadius: 3,
										bgcolor: "grey.200",
										"& .MuiLinearProgress-bar": {
											bgcolor: spentColor,
											borderRadius: 3,
										},
									}}
								/>
								<Typography
									variant="caption"
									color="text.disabled"
									sx={{ mt: 0.5, display: "block" }}
								>
									{spentPct}% of budget utilized
								</Typography>
							</Box>
						</TabPanel>

						{/* ── Vehicles Tab ── */}
						<TabPanel value={tab} index={2}>
							<SectionHead
								count={vehicles.length}
								amt={spendVehicles}
							>
								Vehicles
							</SectionHead>
							{vehicles.length === 0 ? (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ textAlign: "center", py: 3 }}
								>
									No vehicles linked to this funding source.
								</Typography>
							) : (
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell sx={thStyle}>
												Unit
											</TableCell>
											<TableCell sx={thStyle}>
												Vehicle
											</TableCell>
											<TableCell sx={thStyle}>
												Status
											</TableCell>
											<TableCell
												sx={{
													...thStyle,
													textAlign: "right",
												}}
											>
												Cost
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{vehicles.map((v) => (
											<TableRow key={v.id}>
												<TableCell
													sx={{
														...tdStyle,
														fontFamily: "monospace",
													}}
												>
													{v.unitNumber}
												</TableCell>
												<TableCell sx={tdStyle}>
													{v.year} {v.make} {v.model}
												</TableCell>
												<TableCell sx={tdStyle}>
													<StatusChip
														status={v.status}
													/>
												</TableCell>
												<TableCell
													sx={{
														...tdStyle,
														textAlign: "right",
														fontFamily: "monospace",
													}}
												>
													{fmtC(
														v.originalCost ||
															v.cost,
													)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</TabPanel>

						{/* ── Equipment Tab ── */}
						<TabPanel value={tab} index={3}>
							<SectionHead
								count={equipment.length}
								amt={spendEquipment}
							>
								Equipment
							</SectionHead>
							{equipment.length === 0 ? (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ textAlign: "center", py: 3 }}
								>
									No equipment linked to this funding source.
								</Typography>
							) : (
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell sx={thStyle}>
												Property #
											</TableCell>
											<TableCell sx={thStyle}>
												Item
											</TableCell>
											<TableCell sx={thStyle}>
												Status
											</TableCell>
											<TableCell
												sx={{
													...thStyle,
													textAlign: "right",
												}}
											>
												Cost
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{equipment.map((e) => (
											<TableRow key={e.id}>
												<TableCell
													sx={{
														...tdStyle,
														fontFamily: "monospace",
														fontSize: 11,
													}}
												>
													{e.propertyNumber || "—"}
												</TableCell>
												<TableCell sx={tdStyle}>
													{e.type}
													{e.brand
														? ` — ${e.brand}`
														: ""}
													{e.model
														? ` ${e.model}`
														: ""}
												</TableCell>
												<TableCell sx={tdStyle}>
													<StatusChip
														status={e.condition}
													/>
												</TableCell>
												<TableCell
													sx={{
														...tdStyle,
														textAlign: "right",
														fontFamily: "monospace",
													}}
												>
													{fmtC(e.cost)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</TabPanel>

						{/* ── Work Orders Tab ── */}
						<TabPanel value={tab} index={4}>
							<SectionHead
								count={workOrders.length}
								amt={spendWOs}
							>
								Work Orders
							</SectionHead>
							{workOrders.length === 0 ? (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ textAlign: "center", py: 3 }}
								>
									No work orders linked to this funding
									source.
								</Typography>
							) : (
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell sx={thStyle}>
												WO #
											</TableCell>
											<TableCell sx={thStyle}>
												Description
											</TableCell>
											<TableCell sx={thStyle}>
												Status
											</TableCell>
											<TableCell
												sx={{
													...thStyle,
													textAlign: "right",
												}}
											>
												Cost
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{workOrders.map((w) => (
											<TableRow key={w.id}>
												<TableCell
													sx={{
														...tdStyle,
														fontFamily: "monospace",
													}}
												>
													WO-
													{String(w.id).padStart(
														4,
														"0",
													)}
												</TableCell>
												<TableCell sx={tdStyle}>
													{w.notes?.slice(0, 50) ||
														w.type}
												</TableCell>
												<TableCell sx={tdStyle}>
													<StatusChip
														status={w.status}
													/>
												</TableCell>
												<TableCell
													sx={{
														...tdStyle,
														textAlign: "right",
														fontFamily: "monospace",
													}}
												>
													{fmtC(w.cost)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</TabPanel>

						{/* ── Maintenance Tab ── */}
						<TabPanel value={tab} index={5}>
							<SectionHead
								count={maintenance.length}
								amt={spendMaint}
							>
								Maintenance
							</SectionHead>
							{maintenance.length === 0 ? (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ textAlign: "center", py: 3 }}
								>
									No maintenance records linked to this
									funding source.
								</Typography>
							) : (
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell sx={thStyle}>
												Date
											</TableCell>
											<TableCell sx={thStyle}>
												Description
											</TableCell>
											<TableCell sx={thStyle}>
												Status
											</TableCell>
											<TableCell
												sx={{
													...thStyle,
													textAlign: "right",
												}}
											>
												Cost
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{maintenance.map((mr) => (
											<TableRow key={mr.id}>
												<TableCell
													sx={{
														...tdStyle,
														fontFamily: "monospace",
														fontSize: 11,
													}}
												>
													{fmtDate(mr.date)}
												</TableCell>
												<TableCell sx={tdStyle}>
													{mr.description?.slice(
														0,
														50,
													)}
												</TableCell>
												<TableCell sx={tdStyle}>
													<StatusChip
														status={mr.status}
													/>
												</TableCell>
												<TableCell
													sx={{
														...tdStyle,
														textAlign: "right",
														fontFamily: "monospace",
													}}
												>
													{fmtC(mr.cost)}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							)}
						</TabPanel>

						{/* ── Dispensable Items Tab ── */}
						<TabPanel value={tab} index={6}>
							<SectionHead
								count={dispensableRestocksWithItems.length}
								amt={spendDisp}
							>
								Dispensable Items — Restocks
							</SectionHead>
							{dispensableRestocksWithItems.length === 0 ? (
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ textAlign: "center", py: 3 }}
								>
									No dispensable restocks linked to this
									funding source.
								</Typography>
							) : (
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell sx={thStyle}>
												Date
											</TableCell>
											<TableCell sx={thStyle}>
												Tracking #
											</TableCell>
											<TableCell sx={thStyle}>
												Item
											</TableCell>
											<TableCell sx={thStyle}>
												Vendor
											</TableCell>
											<TableCell
												sx={{
													...thStyle,
													textAlign: "right",
												}}
											>
												Qty
											</TableCell>
											<TableCell
												sx={{
													...thStyle,
													textAlign: "right",
												}}
											>
												Purchase Cost
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{dispensableRestocksWithItems.map(
											(iss) => (
												<TableRow key={iss.id}>
													<TableCell
														sx={{
															...tdStyle,
															fontFamily:
																"monospace",
														}}
													>
														{fmtDate(iss.date)}
													</TableCell>
													<TableCell
														sx={{
															...tdStyle,
															fontFamily:
																"monospace",
															fontSize: 11,
														}}
													>
														{
															iss.item
																.trackingNumber
														}
													</TableCell>
													<TableCell sx={tdStyle}>
														{iss.item.description}
													</TableCell>
													<TableCell
														sx={{
															...tdStyle,
															color: "text.secondary",
															fontSize: 11,
														}}
													>
														{iss.item.vendor || "—"}
													</TableCell>
													<TableCell
														sx={{
															...tdStyle,
															textAlign: "right",
															fontFamily:
																"monospace",
														}}
													>
														{Math.abs(iss.quantity)}
													</TableCell>
													<TableCell
														sx={{
															...tdStyle,
															textAlign: "right",
															fontFamily:
																"monospace",
														}}
													>
														{fmtC(iss.purchaseCost)}
													</TableCell>
												</TableRow>
											),
										)}
									</TableBody>
								</Table>
							)}
						</TabPanel>
					</>
				)}
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				<PATPrintButton onClick={handlePrint} tooltipText="Print" />
				<PATCloseButton onClick={onClose} tooltipText="Close" />
				<PATEditButton
					onClick={() => onEdit(record)}
					tooltipText="Edit"
				/>
			</DialogActions>
		</Dialog>
	);
};

export default FundingDetailDialog;
